import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAssistantStore, type PageContext } from '@/stores/assistantStore';

/**
 * Watches the current route and extracts entity context
 * (e.g. /vehicles/abc-123 → entityType: 'vehicle', entityId: 'abc-123';
 * /parties/persons/abc-123 → entityType: 'person', entityId: 'abc-123')
 */

const ENTITY_ROUTES: Record<string, string> = {
  vehicles: 'vehicle',
  bookings: 'booking',
  workorders: 'workOrder',
  inspections: 'inspection',
};

// Persons and Companies detail routes live one level deeper than other
// entities, under /parties/{persons|companies}/:id (issue #1014's
// Persons/Companies → Customers consolidation).
const PARTY_SUB_ENTITY_ROUTES: Record<string, string> = {
  persons: 'person',
  companies: 'company',
};

// The quote queue is the Quotes tab of /workorders (#3788). It reports the
// route it had before, /quotes, so the assistant still offers its quote tools.
function routeOf(pathname: string, search: string): string {
  if (pathname === '/workorders' && new URLSearchParams(search).get('tab') === 'quotes') return '/quotes';
  return pathname;
}

function parseRouteContext(pathname: string, search = ''): PageContext {
  const segments = pathname.split('/').filter(Boolean);

  const context: PageContext = { route: routeOf(pathname, search) };

  if (segments[0] === 'parties' && segments.length >= 3) {
    const entityType = PARTY_SUB_ENTITY_ROUTES[segments[1]!];
    const possibleId = segments[2]!;
    if (entityType && /^[0-9a-f-]{36}$/i.test(possibleId)) {
      context.entityType = entityType;
      context.entityId = possibleId;
    }
    return context;
  }

  // Match /{entityType}/{entityId} pattern
  if (segments.length >= 2) {
    const entityKey = segments[0]!;
    const entityType = ENTITY_ROUTES[entityKey];
    if (entityType) {
      context.entityType = entityType;
      // Check if second segment looks like a UUID
      const possibleId = segments[1]!;
      if (/^[0-9a-f-]{36}$/i.test(possibleId)) {
        context.entityId = possibleId;
      }
    }
  }

  return context;
}

export function AssistantContextProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const setCurrentContext = useAssistantStore((s) => s.setCurrentContext);

  useEffect(() => {
    const context = parseRouteContext(location.pathname, location.search);
    setCurrentContext(context);
  }, [location.pathname, location.search, setCurrentContext]);

  return <>{children}</>;
}
